import Phaser from 'phaser';

declare const __BUILD_TIME__: string;

export type SoundKey = 'button_click' | 'kill' | 'conquer' | 'death' | 'warning' | 'rank_up';
export type MusicKey = 'lobby_bgm' | 'game_bgm';
export type BgmMode = 'auto' | 'manual' | 'random';

const LS_BGM_MODE = 'landlock_bgm_mode';
const LS_BGM_HOUR = 'landlock_bgm_hour';
const LS_BGM_VOL = 'landlock_bgm_vol';
const LS_SFX_VOL = 'landlock_sfx_vol';

export class AudioManager {
  private static instance: AudioManager;
  private scene: Phaser.Scene;
  private sounds: Map<SoundKey, Phaser.Sound.BaseSound> = new Map();
  private music: Map<MusicKey, Phaser.Sound.BaseSound> = new Map();
  private currentMusicKey?: MusicKey;
  private musicUnlockArmed: boolean = false;
  private isMuted: boolean = false;
  private musicMuted: boolean = false;
  private sfxMuted: boolean = false;
  private volume: number = 0.5;
  private musicVolume: number = 0.28;
  private sfxVolume: number = 0.4;
  // 시간대별 BGM(동물의 숲 24시간): 현재 시각 트랙을 동적 로드해 재생, 정시마다 교체
  private hourlyMode: boolean = false;
  private currentHourlyKey?: string;
  private hourlyTrack?: Phaser.Sound.BaseSound;
  private hourTimer?: ReturnType<typeof setInterval>;
  private manualHour: number | null = null; // 수동 선택된 시각(null=자동/시간대)
  private bgmMode: BgmMode = 'auto'; // auto(시간대)/manual(특정곡)/random(랜덤)

  private constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.musicMuted = this.readStoredMusicMuted();
    this.sfxMuted = this.readStoredSfxMuted();
    this.loadStoredBgmSettings();
    this.initSounds();
  }

  private loadStoredBgmSettings() {
    try {
      const m = window.localStorage.getItem(LS_BGM_MODE);
      if (m === 'auto' || m === 'manual' || m === 'random') this.bgmMode = m;
      const h = parseInt(window.localStorage.getItem(LS_BGM_HOUR) ?? '', 10);
      if (!Number.isNaN(h)) this.manualHour = ((h % 24) + 24) % 24;
      const bv = parseFloat(window.localStorage.getItem(LS_BGM_VOL) ?? '');
      if (!Number.isNaN(bv)) this.musicVolume = Math.max(0, Math.min(1, bv));
      const sv = parseFloat(window.localStorage.getItem(LS_SFX_VOL) ?? '');
      if (!Number.isNaN(sv)) this.sfxVolume = Math.max(0, Math.min(1, sv));
    } catch { /* storage 사용 불가 무시 */ }
  }

  public static getInstance(scene?: Phaser.Scene): AudioManager {
    if (!AudioManager.instance && scene) {
      AudioManager.instance = new AudioManager(scene);
    } else if (AudioManager.instance && scene) {
      AudioManager.instance.scene = scene;
      AudioManager.instance.initSounds();
    }
    return AudioManager.instance;
  }

  private initSounds() {
    const soundKeys: SoundKey[] = ['button_click', 'kill', 'conquer', 'death', 'warning', 'rank_up'];
    const musicKeys: MusicKey[] = ['lobby_bgm', 'game_bgm'];

    soundKeys.forEach((key) => {
      try {
        if (!this.sounds.has(key) && this.scene.cache.audio.exists(key)) {
          const sound = this.scene.sound.add(key, { volume: this.sfxVolume * this.volume });
          this.sounds.set(key, sound);
        } else {
          if (!this.sounds.has(key)) console.warn(`Audio file not found: ${key}`);
        }
      } catch (e) {
        console.warn(`Failed to load sound: ${key}`, e);
      }
    });

    musicKeys.forEach((key) => {
      try {
        if (!this.music.has(key) && this.scene.cache.audio.exists(key)) {
          const track = this.scene.sound.add(key, {
            volume: this.musicVolume * this.volume,
            loop: true,
          });
          this.music.set(key, track);
        } else {
          if (!this.music.has(key)) console.warn(`Music file not found: ${key}`);
        }
      } catch (e) {
        console.warn(`Failed to load music: ${key}`, e);
      }
    });
  }

  public play(key: SoundKey) {
    if (this.isMuted || this.sfxMuted) return;

    const sound = this.sounds.get(key);
    if (sound) {
      sound.play();
    }
  }

  public playMusic(key: MusicKey) {
    this.currentMusicKey = key;
    if (this.isMuted || this.musicMuted) return;

    this.music.forEach((track, trackKey) => {
      if (trackKey !== key && track.isPlaying) {
        track.stop();
      }
    });

    const track = this.music.get(key);
    if (!track || track.isPlaying) return;

    if ((this.scene.sound as any).locked) {
      this.armMusicUnlock();
      return;
    }

    try {
      track.play({ loop: true, volume: this.musicVolume * this.volume });
    } catch (e) {
      console.warn(`Failed to play music: ${key}`, e);
    }
  }

  /** 씬 진입 시 호출: 현재 설정(모드)대로 시간대 BGM 재생. */
  public startBgm() {
    this.hourlyMode = true;
    this.currentMusicKey = undefined;
    this.ensureHourWatcher();
    if (this.isMuted || this.musicMuted) return;
    this.music.forEach((track) => { if (track.isPlaying) track.stop(); }); // 표준 BGM 정지
    if (this.bgmMode === 'random') { this.playRandom(); return; }
    const hour = this.bgmMode === 'manual' ? (this.manualHour ?? new Date().getHours()) : new Date().getHours();
    const key = `bgm_hour_${String(((hour % 24) + 24) % 24).padStart(2, '0')}`;
    this.currentHourlyKey = key;
    this.startHourlyTrack(key, true);
  }

  /** 설정에서 BGM 모드 선택(auto/manual/random). 뮤트면 해제하고 새로 시작. */
  public setBgmMode(mode: BgmMode, hour?: number) {
    this.bgmMode = mode;
    this.manualHour = mode === 'manual' ? (hour != null ? ((hour % 24) + 24) % 24 : (this.manualHour ?? 0)) : null;
    try {
      window.localStorage.setItem(LS_BGM_MODE, mode);
      window.localStorage.setItem(LS_BGM_HOUR, String(this.manualHour ?? 0));
    } catch { /* noop */ }
    if (this.musicMuted) { this.musicMuted = false; this.writeStoredMusicMuted(false); }
    this.hourlyTrack?.stop();
    this.startBgm();
  }

  private playRandom() {
    if (this.hourlyTrack?.isPlaying) return; // 재생 중이면 유지(씬 전환 끊김 방지)
    this.startRandomTrack();
  }

  private startRandomTrack() {
    const key = `bgm_hour_${String(Math.floor(Math.random() * 24)).padStart(2, '0')}`;
    this.currentHourlyKey = key;
    this.startHourlyTrack(key, false, () => {
      if (this.bgmMode === 'random' && !this.isMuted && !this.musicMuted) this.startRandomTrack();
    });
  }

  public getBgmMode(): BgmMode { return this.bgmMode; }
  public getSelectedHour(): number | null { return this.manualHour; }
  public getBgmVolume() { return this.musicVolume; }
  public getSfxVolume() { return this.sfxVolume; }

  public setBgmVolume(v: number) {
    this.musicVolume = Math.max(0, Math.min(1, v));
    try { window.localStorage.setItem(LS_BGM_VOL, String(this.musicVolume)); } catch { /* noop */ }
    const vol = this.musicVolume * this.volume;
    if (this.hourlyTrack && 'setVolume' in this.hourlyTrack) (this.hourlyTrack as Phaser.Sound.WebAudioSound).setVolume(vol);
    this.music.forEach((t) => { if ('setVolume' in t) (t as Phaser.Sound.WebAudioSound).setVolume(vol); });
  }

  public setSfxVolume(v: number) {
    this.sfxVolume = Math.max(0, Math.min(1, v));
    try { window.localStorage.setItem(LS_SFX_VOL, String(this.sfxVolume)); } catch { /* noop */ }
    const vol = this.sfxVolume * this.volume;
    this.sounds.forEach((s) => { if ('setVolume' in s) (s as Phaser.Sound.WebAudioSound).setVolume(vol); });
  }

  private startHourlyTrack(key: string, loop = true, onComplete?: () => void) {
    const cur = this.hourlyTrack as (Phaser.Sound.BaseSound & { key?: string }) | undefined;
    if (cur && cur.key === key && cur.isPlaying && loop) return; // 같은 곡 루프 중이면 유지
    if (cur && cur.key !== key) { cur.stop(); cur.destroy(); this.hourlyTrack = undefined; }

    if (!this.scene.cache.audio.exists(key)) {
      const ver = typeof __BUILD_TIME__ !== 'undefined' ? `?v=${encodeURIComponent(__BUILD_TIME__)}` : '';
      this.scene.load.audio(key, `/assets/audio/${key}.mp3${ver}`);
      this.scene.load.once(`filecomplete-audio-${key}`, () => {
        if (this.hourlyMode && this.currentHourlyKey === key && !this.isMuted && !this.musicMuted) {
          this.startHourlyTrack(key, loop, onComplete);
        }
      });
      this.scene.load.start();
      return;
    }
    if ((this.scene.sound as any).locked) { this.armMusicUnlock(); return; }
    try {
      const track = this.scene.sound.add(key, { volume: this.musicVolume * this.volume, loop });
      this.hourlyTrack = track;
      if (onComplete) track.once('complete', onComplete);
      track.play({ loop, volume: this.musicVolume * this.volume });
    } catch (e) {
      console.warn(`Failed to play hourly bgm: ${key}`, e);
    }
  }

  /** 정시 자동 전환: 30초마다 시각을 확인해 hour가 바뀌면 해당 트랙으로 교체. */
  private ensureHourWatcher() {
    if (this.hourTimer !== undefined) return;
    this.hourTimer = setInterval(() => {
      if (!this.hourlyMode || this.isMuted || this.musicMuted) return;
      if (this.bgmMode !== 'auto') return; // 자동 모드에서만 정시 전환
      const key = `bgm_hour_${String(new Date().getHours()).padStart(2, '0')}`;
      if (key !== this.currentHourlyKey) {
        this.currentHourlyKey = key;
        this.startHourlyTrack(key, true);
      }
    }, 30000);
  }

  private armMusicUnlock() {
    if (this.musicUnlockArmed) return;
    this.musicUnlockArmed = true;

    const resume = () => {
      this.musicUnlockArmed = false;
      if (this.isMuted || this.musicMuted) return;
      window.setTimeout(() => {
        if (this.isMuted || this.musicMuted) return;
        this.startBgm();
      }, 0);
    };

    document.addEventListener('pointerdown', resume, { once: true });
    document.addEventListener('keydown', resume, { once: true });
  }

  public stopMusic(key?: MusicKey) {
    if (key) {
      this.music.get(key)?.stop();
      if (this.currentMusicKey === key) this.currentMusicKey = undefined;
      return;
    }

    this.music.forEach((track) => track.stop());
    this.currentMusicKey = undefined;
  }

  public setMuted(muted: boolean) {
    this.isMuted = muted;
    if (muted) {
      this.music.forEach((track) => track.stop());
      this.hourlyTrack?.stop();
    } else if (!this.musicMuted) {
      this.startBgm();
    }
  }

  public setMusicMuted(muted: boolean) {
    this.musicMuted = muted;
    this.writeStoredMusicMuted(muted);

    if (muted) {
      this.music.forEach((track) => track.stop());
      this.hourlyTrack?.stop();
      return;
    }

    if (!this.isMuted) {
      this.startBgm();
    }
  }

  public toggleMusicMuted(): boolean {
    this.setMusicMuted(!this.musicMuted);
    return this.musicMuted;
  }

  public setSfxMuted(muted: boolean) {
    this.sfxMuted = muted;
    this.writeStoredSfxMuted(muted);
  }

  public toggleSfxMuted(): boolean {
    this.setSfxMuted(!this.sfxMuted);
    return this.sfxMuted;
  }

  public toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    return this.isMuted;
  }

  public setVolume(volume: number) {
    this.volume = Phaser.Math.Clamp(volume, 0, 1);

    this.sounds.forEach((sound) => {
      if ('setVolume' in sound) {
        (sound as Phaser.Sound.WebAudioSound).setVolume(this.sfxVolume * this.volume);
      }
    });

    this.music.forEach((track) => {
      if ('setVolume' in track) {
        (track as Phaser.Sound.WebAudioSound).setVolume(this.musicVolume * this.volume);
      }
    });

    if (this.hourlyTrack && 'setVolume' in this.hourlyTrack) {
      (this.hourlyTrack as Phaser.Sound.WebAudioSound).setVolume(this.musicVolume * this.volume);
    }
  }

  public getVolume(): number {
    return this.volume;
  }

  public isSoundMuted(): boolean {
    return this.isMuted;
  }

  public isMusicMuted(): boolean {
    return this.musicMuted;
  }

  public isSfxMuted(): boolean {
    return this.sfxMuted;
  }

  private readStoredMusicMuted(): boolean {
    try {
      return window.localStorage.getItem('landlock_bgm_muted') === 'true';
    } catch {
      return false;
    }
  }

  private writeStoredMusicMuted(muted: boolean) {
    try {
      window.localStorage.setItem('landlock_bgm_muted', muted ? 'true' : 'false');
    } catch {
      // Storage can be unavailable in private contexts; the in-memory state still works.
    }
  }

  private readStoredSfxMuted(): boolean {
    try {
      return window.localStorage.getItem('landlock_sfx_muted') === 'true';
    } catch {
      return false;
    }
  }

  private writeStoredSfxMuted(muted: boolean) {
    try {
      window.localStorage.setItem('landlock_sfx_muted', muted ? 'true' : 'false');
    } catch {
      // Storage can be unavailable in private contexts; the in-memory state still works.
    }
  }
}
